import {
	Payment,
	PaymentType,
	Transaction,
} from "@crypto-payment-gateway/models"
import { formatDate } from "../../../../utils/format"
import {
	CryptoParty,
	FiatParty,
	TaxDescription,
	Time,
	TransactionTax,
} from "./types"
import {
	findDisplayNameAndImgForBankInJson,
	getPspOrBankId,
} from "../../../../lib/utils"

// Update the getFormattedDateTime function
export function formatDateTime(timestamp: string) {
	try {
		const dateTime = new Date(timestamp)
		return {
			date: formatDate(dateTime),
			time: dateTime
				.toLocaleTimeString("es-AR", {
					hour: "2-digit",
					minute: "2-digit",
					hour12: true,
				})
				.replace("a. m.", "AM")
				.replace("p. m.", "PM"),
		}
	} catch (error) {
		console.error("Error formatting datetime:", error)
		return {
			date: formatDate(new Date()),
			time: new Date()
				.toLocaleTimeString("es-AR", {
					hour: "2-digit",
					minute: "2-digit",
					hour12: true,
				})
				.replace("a. m.", "AM")
				.replace("p. m.", "PM"),
		}
	}
}

// Add this helper function near the top of the file
export function parseFormattedDateTimeString(
	dateStr: string,
	timeStr: string
): Date {
	try {
		// Parse DD/MM/YY date format
		const [day, month, year] = dateStr.split("/")
		const [time, period] = timeStr.split(" ") // e.g., "08:22 PM"
		const [hours, minutes] = time.split(":")

		// Convert hours to 24-hour format if PM
		let hour = parseInt(hours)
		if (period === "PM" && hour !== 12) {
			hour += 12
		} else if (period === "AM" && hour === 12) {
			hour = 0
		}

		// Create date object with full year (assuming 20xx for YY format)
		const fullYear = 2000 + parseInt(year)
		const date = new Date(
			fullYear,
			parseInt(month) - 1,
			parseInt(day),
			hour,
			parseInt(minutes)
		)

		if (isNaN(date.getTime())) {
			console.error("Invalid date parsing:", { dateStr, timeStr })
			return new Date(0)
		}

		return date
	} catch (error) {
		console.error("Error parsing date:", { dateStr, timeStr, error })
		return new Date(0)
	}
}

export function isTransferPayment(payment: Payment | Transaction): boolean {
	if (
		"payment_options" in payment &&
		payment.payment_options.includes("transfer" as PaymentType)
	) {
		return true
	}

	return (
		"currency" in payment &&
		"network" in payment &&
		payment.currency === "ARS" &&
		payment.network === "POLLUX"
	)
}

export function convertToTime(timestamp: string | undefined): Time | undefined {
	if (!timestamp) {
		return undefined
	}
	const result = {
		time: new Date(timestamp),
		formatted: formatDateTime(timestamp),
	} as const
	return result satisfies Time
}

export function createErrorTime(): Time {
	console.error("Error creating time object, using current time instead")
	const now = new Date()
	return {
		time: now,
		formatted: { date: "ERROR", time: "ERROR" },
	}
}

export function extractPartyInfoArs(
	transaction: any,
	type: "sender" | "recipient"
): FiatParty {
	const processedData = transaction?.transaction_data?.PROCESSED || {}
	const prefix = type === "sender" ? "sender" : "recipient"
	const address =
		type === "sender" ? transaction?.sender_address : transaction?.address

	// Get bank/psp info if address exists
	let bankEntity: FiatParty["bank_entity"] = undefined
	if (address) {
		const [entityId, entityType] = getPspOrBankId(address)
		const { bank_img, bank_name } = findDisplayNameAndImgForBankInJson(entityId)
		bankEntity = {
			entity_type: entityType === "psp_id" ? "psp" : ("bank" as const),
			id: entityId,
			img: bank_img,
			name: bank_name,
		}
	}

	return {
		cuit: processedData[`${prefix}Cuit`],
		cvu: address,
		titular: processedData[`${prefix}Titular`],
		alias:
			type === "recipient"
				? transaction?.alias
				: processedData[`${prefix}Alias`],
		bank_entity: bankEntity,
		type: "fiat",
	}
}

export function extractTransactionPartiesArs(payment: any): {
	sender: FiatParty
	recipient: FiatParty
} {
	// First try to get from first transaction
	const transaction = payment.transactions?.[0] || {}
	const quote = payment.quotes?.[0] || {}

	return {
		sender: extractPartyInfoArs(transaction, "sender"),
		recipient: extractPartyInfoArs({ ...transaction, ...quote }, "recipient"),
	}
}

export function extractTransactionPartiesCrypto(payment: any): {
	sender: CryptoParty
	recipient: CryptoParty
} {
	// First try to get from first transaction
	const transaction = payment.transactions?.[0] || {}
	const quote = payment.quotes?.[0] || {}

	return {
		sender: {
			address:
				transaction.sender_address ||
				payment.transaction_fields?.sender_address,
			type: "crypto",
		},
		recipient: {
			address:
				transaction.address ||
				quote.address ||
				payment.transaction_fields?.address,
			type: "crypto",
		},
	}
}

export function extractTransactionTaxesFromOrder(
	payment: any
): TransactionTax | undefined {
	if (payment.transactions) {
		// Collect all taxes from transactions
		const taxes = payment.transactions.reduce(
			(acc: TaxDescription[], transaction: any) => {
				if (transaction.taxes) {
					acc.push(...transaction.taxes)
				}
				return acc
			},
			[]
		)

		// Calculate total tax amount
		const totalTax = taxes.reduce(
			(acc: number, tax: { tax_amount: string }) =>
				acc + Number(tax.tax_amount),
			0
		)

		return {
			taxes,
			total_tax_amount: String(totalTax),
		}
	}
	return undefined
}
